import type { BrandLearningSourceSummary } from '@mondaygpt/types'
import { Button } from 'components'
import { cn } from 'lib/utils'
import { X } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { isOverridden, readOverride } from './_internals/override-utils'
import { useInflightSet } from './_internals/use-inflight'

/**
 * "How you sound" — the voice half of the brand-learning summary surface.
 *
 * The mined voice samples and the Length/Emoji/Hashtags/Pronoun chips were
 * intentionally removed from the UI — raw Instagram captions felt weird to
 * present as "your voice" (mixed languages, emoji, hashtags), and the chip
 * jargon (Pronoun: Mixed, Hashtags: ~1) wasn't actionable. The signals still
 * flow through the backend and steer generation; we just don't surface them.
 *
 * On `card` variant (onboarding) the section now renders nothing — there is
 * no page-only editor left to show inside the onboarding card.
 * On `page` variant (Brand Kit) we keep the deeper editors: Never say,
 * Signature phrases, Hashtag bank, CTA patterns.
 */
export interface BrandVoiceSectionProps {
  /** Per-source summary. `null` = no data yet. */
  summary: BrandLearningSourceSummary | null
  /**
   * Accepted but currently ignored — the only consumer was the voice-samples
   * loading skeleton, which was stripped. Kept on the interface so existing
   * callers don't have to change their JSX while the page-variant editors
   * still need a place to grow loading state back if we ever re-add it.
   */
  isLoading?: boolean
  onOverride: (fieldPath: string, value: unknown) => Promise<void>
  onClearOverride: (fieldPath: string) => Promise<void>
  /** `card` = compact for BrandCardStep, `page` = full editor for BrandKitPage. */
  variant: 'card' | 'page'
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function sourceHeader(source: BrandLearningSourceSummary | null): {
  title: string
  subtitle: string
} {
  if (!source || !source.aggregates) {
    return {
      title: 'How you sound',
      subtitle: "We'll surface your voice signals here once mining finishes.",
    }
  }
  if (source.source === 'instagram') {
    return {
      title: 'How you sound',
      subtitle: 'Based on your last ~25 Instagram posts.',
    }
  }
  if (source.source === 'website') {
    return {
      title: 'How you sound',
      subtitle: 'Based on your homepage.',
    }
  }
  return {
    title: 'How you sound',
    subtitle: `Based on your ${source.source} signals.`,
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

export const BrandVoiceSection: React.FC<BrandVoiceSectionProps> = ({
  summary,
  onOverride,
  onClearOverride,
  variant,
}) => {
  const { title, subtitle } = sourceHeader(summary)
  const isPage = variant === 'page'

  const aggregates = summary?.aggregates ?? null
  const overrides = summary?.userOverrides ?? {}

  const inflight = useInflightSet()

  const handleChipChange = useCallback(
    (fieldPath: string, value: unknown) =>
      inflight.run(fieldPath, () => onOverride(fieldPath, value)),
    [onOverride, inflight]
  )

  const handleChipClear = useCallback(
    (fieldPath: string) => inflight.run(fieldPath, () => onClearOverride(fieldPath)),
    [onClearOverride, inflight]
  )

  // Page-only list values
  const neverSay =
    readOverride<string[]>(overrides, 'voice.neverSay') ?? aggregates?.voice.neverSay ?? []
  const signaturePhrases =
    readOverride<string[]>(overrides, 'voice.signaturePhrases') ??
    aggregates?.voice.signaturePhrases ??
    []
  const hashtagBank =
    readOverride<string[]>(overrides, 'voice.hashtagBank') ?? aggregates?.voice.hashtagBank ?? []
  const ctaPatterns =
    readOverride<{ pattern: string; count: number }[]>(overrides, 'voice.ctaPatterns') ??
    aggregates?.voice.ctaPatterns ??
    []

  // Onboarding card has nothing left to render after the voice-samples + style
  // chips strip. Suppress the section entirely instead of leaving an orphan
  // header.
  if (!isPage) return null

  return (
    <section className={cn('flex flex-col gap-4', isPage && 'card p-6')}>
      <header className="flex flex-col gap-1">
        <h3 className="text-[14px] font-semibold tracking-[-0.005em] text-paper">{title}</h3>
        <p className="text-[12px] leading-[1.5] text-paper-dim">{subtitle}</p>
      </header>

      <ListEditor
        label="Never say"
        emptyCopy="No words to avoid yet. Add brand no-go phrases here."
        values={neverSay}
        fieldPath="voice.neverSay"
        isBusy={inflight.has('voice.neverSay')}
        isEdited={isOverridden(overrides, 'voice.neverSay')}
        onChange={handleChipChange}
        onClear={handleChipClear}
        allowAdd
        placeholder="Add a word or phrase…"
      />
      <ListEditor
        label="Signature phrases"
        emptyCopy="No signature phrases detected yet."
        values={signaturePhrases}
        fieldPath="voice.signaturePhrases"
        isBusy={inflight.has('voice.signaturePhrases')}
        isEdited={isOverridden(overrides, 'voice.signaturePhrases')}
        onChange={handleChipChange}
        onClear={handleChipClear}
        allowAdd
        placeholder="Add a phrase…"
      />
      <ListEditor
        label="Hashtag bank"
        emptyCopy="No hashtags yet."
        values={hashtagBank}
        fieldPath="voice.hashtagBank"
        isBusy={inflight.has('voice.hashtagBank')}
        isEdited={isOverridden(overrides, 'voice.hashtagBank')}
        onChange={handleChipChange}
        onClear={handleChipClear}
        allowAdd
        placeholder="Add a hashtag (without #)…"
        formatValue={v => (v.startsWith('#') ? v : `#${v}`)}
      />
      <CtaPatternsList
        patterns={ctaPatterns}
        isBusy={inflight.has('voice.ctaPatterns')}
        isEdited={isOverridden(overrides, 'voice.ctaPatterns')}
        onChange={handleChipChange}
        onClear={handleChipClear}
      />
    </section>
  )
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

interface ListEditorProps {
  label: string
  emptyCopy: string
  values: string[]
  fieldPath: string
  isBusy: boolean
  isEdited: boolean
  /** Normalize a typed value before saving (e.g. prepend `#` for hashtags). */
  formatValue?: (raw: string) => string
  placeholder?: string
  allowAdd?: boolean
  onChange: (fieldPath: string, value: unknown) => Promise<void>
  onClear: (fieldPath: string) => Promise<void>
}

/**
 * Chip-style add/remove list. Overrides the entire array on the server every
 * mutation — small arrays + low write volume so the round-trip cost is fine.
 */
const ListEditor: React.FC<ListEditorProps> = ({
  label,
  emptyCopy,
  values,
  fieldPath,
  isBusy,
  isEdited,
  formatValue,
  placeholder = 'Add an item…',
  allowAdd = true,
  onChange,
  onClear,
}) => {
  const [draft, setDraft] = useState('')

  const handleAdd = async () => {
    const raw = draft.trim()
    if (!raw) return
    const normalized = formatValue ? formatValue(raw) : raw
    if (values.includes(normalized)) {
      toast.info('Already in the list.')
      return
    }
    setDraft('')
    await onChange(fieldPath, [...values, normalized])
  }

  const handleRemove = async (idx: number) => {
    const next = values.slice()
    next.splice(idx, 1)
    await onChange(fieldPath, next)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <div className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">{label}</div>
        {isEdited && (
          <button
            type="button"
            onClick={() => {
              void onClear(fieldPath)
            }}
            className="text-[11px] text-paper-faint underline underline-offset-2 hover:text-paper"
            disabled={isBusy}
          >
            Reset
          </button>
        )}
      </div>
      {values.length === 0 ? (
        <span className="text-[12px] text-paper-faint">{emptyCopy}</span>
      ) : (
        <div className="flex flex-wrap gap-2">
          {values.map((v, i) => (
            <span
              key={`${v}-${i}`}
              className="chip inline-flex items-center gap-1.5"
              data-active={isEdited}
            >
              <span>{v}</span>
              <button
                type="button"
                onClick={() => {
                  void handleRemove(i)
                }}
                disabled={isBusy}
                className="text-paper-faint hover:text-paper disabled:opacity-50"
                aria-label={`Remove ${v}`}
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
      {allowAdd && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void handleAdd()
              }
            }}
            placeholder={placeholder}
            disabled={isBusy}
            className="flex-1 rounded-[9px] border border-line-bright bg-bg-3 px-3 py-2 text-[13px] text-paper transition-[border-color] outline-none placeholder:text-paper-faint focus:border-primary! disabled:opacity-50"
          />
          <Button
            variant="t3-tertiary"
            size="t3-sm"
            onClick={() => {
              void handleAdd()
            }}
            disabled={isBusy || draft.trim().length === 0}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  )
}

interface CtaPatternsListProps {
  patterns: { pattern: string; count: number }[]
  isBusy: boolean
  isEdited: boolean
  onChange: (fieldPath: string, value: unknown) => Promise<void>
  onClear: (fieldPath: string) => Promise<void>
}

/**
 * CTA patterns are `{ pattern, count }` tuples — count is mined frequency and
 * is read-only here. The user can remove patterns they dislike (rare).
 * Adding is intentionally not exposed in v1 — the count value would be a lie.
 */
const CtaPatternsList: React.FC<CtaPatternsListProps> = ({
  patterns,
  isBusy,
  isEdited,
  onChange,
  onClear,
}) => {
  const handleRemove = async (idx: number) => {
    const next = patterns.slice()
    next.splice(idx, 1)
    await onChange('voice.ctaPatterns', next)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <div className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
          CTA patterns
        </div>
        {isEdited && (
          <button
            type="button"
            onClick={() => {
              void onClear('voice.ctaPatterns')
            }}
            className="text-[11px] text-paper-faint underline underline-offset-2 hover:text-paper"
            disabled={isBusy}
          >
            Reset
          </button>
        )}
      </div>
      {patterns.length === 0 ? (
        <span className="text-[12px] text-paper-faint">No CTA patterns detected yet.</span>
      ) : (
        <div className="flex flex-wrap gap-2">
          {patterns.map((p, i) => (
            <span
              key={`${p.pattern}-${i}`}
              className="chip inline-flex items-center gap-1.5"
              data-active={isEdited}
            >
              <span>{p.pattern}</span>
              <span className="mono text-[10px] text-paper-faint">×{p.count}</span>
              <button
                type="button"
                onClick={() => {
                  void handleRemove(i)
                }}
                disabled={isBusy}
                className="text-paper-faint hover:text-paper disabled:opacity-50"
                aria-label={`Remove ${p.pattern}`}
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
